/**
 * NS Collection Item Web Component
 * 封装 collection item 功能为自定义标签
 * 支持多个组件实例，每个实例独立管理自己的状态
 */
(function() {
  'use strict';

  class NSCollectionItem extends HTMLElement {
    constructor() {
      super();
      this.colorItems = [];
      this.sceneContainer = null;
      // 使用 WeakMap 存储每个实例的事件处理器，便于清理
      this.boundHandlers = new WeakMap();
    }

    connectedCallback() {
      // 使用 requestAnimationFrame 确保 DOM 已完全渲染
      requestAnimationFrame(() => {
        this.init();
      });
    }

    init() {
      // 获取色卡元素和场景容器（作用域限制在当前组件内）
      this.colorItems = Array.from(this.querySelectorAll('.ns-color-item'));
      this.sceneContainer = this.querySelector('.ns-collection-item-scene');
      this.colorTitleContainer = this.querySelector('[data-color-title-container]');
      this.loadingElement = this.querySelector('[data-scene-loading]');
      
      // 获取所有场景图片元素
      this.sceneImages = Array.from(this.sceneContainer ? this.sceneContainer.querySelectorAll('.ns-collection-scene-image') : []);

      // 预加载所有图片
      this.preloadImages();

      // 为每个图片添加加载监听
      this.sceneImages.forEach((img) => {
        // 检查图片是否已加载完成
        if (img.complete && img.naturalHeight !== 0) {
          // 图片已加载完成
          img.removeAttribute('data-loading');
        } else {
          // 图片正在加载
          img.setAttribute('data-loading', 'true');
          img.addEventListener('load', () => {
            img.removeAttribute('data-loading');
            // 如果这是当前显示的图片，隐藏loading
            if (!img.classList.contains('ns-hidden')) {
              this.hideLoading();
            }
          });
          img.addEventListener('error', () => {
            img.removeAttribute('data-loading');
            // 即使加载失败也隐藏loading
            if (!img.classList.contains('ns-hidden')) {
              this.hideLoading();
            }
          });
        }
      });

      // 检查初始显示的图片是否正在加载
      const initialImage = this.sceneImages.find(img => !img.classList.contains('ns-hidden'));
      if (initialImage) {
        if (initialImage.getAttribute('data-loading') === 'true') {
          // 图片正在加载，显示loading
          this.showLoading();
        } else if (!initialImage.complete || initialImage.naturalHeight === 0) {
          // 图片可能还未开始加载，也显示loading
          initialImage.setAttribute('data-loading', 'true');
          this.showLoading();
        }
      }

      // 为每个色卡添加点击事件
      this.colorItems.forEach((item) => {
        // 为每个 item 创建绑定的事件处理器
        const handler = (e) => this.handleColorItemClick(e, item);
        this.boundHandlers.set(item, handler);
        item.addEventListener('click', handler);
      });
    }

    // todo: 观察页面性能，需要优化
    preloadImages() {
      // 预加载所有隐藏的图片，确保切换时无需等待
      this.sceneImages.forEach((img) => {
        const imgUrl = img.src || img.getAttribute('src');
        if (!imgUrl) return;
        
        // 如果图片还没有开始加载，使用 Image 对象预加载
        if (!img.complete || img.naturalHeight === 0) {
          // 创建一个临时的 Image 对象来预加载图片
          // 这样可以确保图片被浏览器缓存，切换时直接使用
          const preloadImg = new Image();
          preloadImg.src = imgUrl;
          
          preloadImg.onload = () => {
            // 预加载完成后，如果原图片还没有加载，可以尝试触发原图片加载
            if (!img.complete) {
              // 通过设置 src 来触发加载（如果浏览器还没有开始加载）
              img.src = imgUrl;
            }
          };
          
          preloadImg.onerror = () => {
            // 预加载失败，静默处理
          };
        }
      });
      
      // 使用 requestIdleCallback 在浏览器空闲时进一步优化预加载
      if (window.requestIdleCallback) {
        requestIdleCallback(() => {
          this.sceneImages.forEach((img) => {
            if (!img.complete && img.src) {
              // 确保所有图片都开始加载
              const link = document.createElement('link');
              link.rel = 'preload';
              link.as = 'image';
              link.href = img.src;
              document.head.appendChild(link);
            }
          });
        }, { timeout: 2000 });
      }
    }

    showLoading() {
      if (this.loadingElement) {
        this.loadingElement.classList.add('active');
      }
    }

    hideLoading() {
      if (this.loadingElement) {
        this.loadingElement.classList.remove('active');
      }
    }

    handleColorItemClick(event, clickedItem) {
      // 只处理当前组件内的色卡，不影响其他组件实例
      const colorIndex = clickedItem.getAttribute('data-color-index');
      const colorTitle = clickedItem.getAttribute('data-color-title');

      // 移除当前 collection item 内所有色卡的 active 类
      this.colorItems.forEach((ci) => {
        ci.classList.remove('active');
      });

      // 添加 active 类到当前项
      clickedItem.classList.add('active');

      // 更新色卡标题显示（确保标题正确更新）
      if (this.colorTitleContainer) {
        const titleText = colorTitle && colorTitle.trim() !== '' ? colorTitle.trim() : '';
        this.colorTitleContainer.textContent = titleText;
        if (titleText) {
          this.colorTitleContainer.classList.add('active');
        } else {
          this.colorTitleContainer.classList.remove('active');
        }
      }

      // 通过显示/隐藏切换场景图（优化性能，避免重新加载）
      if (this.sceneContainer && this.sceneImages.length > 0) {
        // 隐藏所有场景图
        this.sceneImages.forEach((img) => {
          img.classList.add('ns-hidden');
        });
        
        // 显示对应colorIndex的图片
        if (colorIndex) {
          const targetImage = this.sceneImages.find((img) => {
            return img.getAttribute('data-color-index') === colorIndex;
          });
          
          if (targetImage) {
            // 检查图片是否正在加载
            const isLoading = targetImage.getAttribute('data-loading') === 'true';
            
            if (isLoading) {
              // 图片正在加载，显示loading
              this.showLoading();
              // 监听图片加载完成
              const onLoad = () => {
                targetImage.removeAttribute('data-loading');
                this.hideLoading();
                targetImage.removeEventListener('load', onLoad);
                targetImage.removeEventListener('error', onError);
              };
              const onError = () => {
                targetImage.removeAttribute('data-loading');
                this.hideLoading();
                targetImage.removeEventListener('load', onLoad);
                targetImage.removeEventListener('error', onError);
              };
              targetImage.addEventListener('load', onLoad);
              targetImage.addEventListener('error', onError);
            } else {
              // 图片已加载完成，直接隐藏loading
              this.hideLoading();
            }
            
            targetImage.classList.remove('ns-hidden');
          } else {
            this.hideLoading();
          }
        } else {
          this.hideLoading();
        }
      } else {
        this.hideLoading();
      }
    }

    disconnectedCallback() {
      // 清理事件监听器
      this.colorItems.forEach((item) => {
        const handler = this.boundHandlers.get(item);
        if (handler) {
          item.removeEventListener('click', handler);
        }
      });
      this.colorItems = [];
      this.sceneContainer = null;
      this.boundHandlers = new WeakMap();
    }
  }

  // 立即注册自定义元素，确保在 DOM 渲染前可用
  if (typeof customElements !== 'undefined' && !customElements.get('ns-collection-item')) {
    customElements.define('ns-collection-item', NSCollectionItem);
  } else if (typeof customElements === 'undefined') {
    // 如果 customElements 未定义，等待其可用后再注册
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        if (!customElements.get('ns-collection-item')) {
          customElements.define('ns-collection-item', NSCollectionItem);
        }
      });
    } else {
      // DOM 已加载，但 customElements 可能还未定义，稍后重试
      setTimeout(function() {
        if (typeof customElements !== 'undefined' && !customElements.get('ns-collection-item')) {
          customElements.define('ns-collection-item', NSCollectionItem);
        }
      }, 0);
    }
  }
})();

